import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut, Settings, Shield, type LucideIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, emSuporte, setAccessToken } from '@/lib/api.js';
import { sairDoSuporte } from '@/pages/SuportePages.js';
import { roleLabel } from '@/lib/utils.js';

type UserMenuProps = {
  showConfig?: boolean;
};

export function UserMenu({ showConfig }: UserMenuProps) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () =>
      (await api.get('/auth/me')).data as {
        name: string;
        email: string;
        role: string;
        avatar_url: string | null;
      },
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const user = me.data;
  if (!user) {
    return <span style={{ width: 120, height: 36, display: 'inline-block' }} />;
  }

  async function handleLogout() {
    // Em modo suporte, "sair" encerra a sessão de suporte. O /auth/logout
    // revogaria o cookie de quem está dando suporte — a conta errada.
    if (emSuporte()) {
      await sairDoSuporte();
      return;
    }
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    localStorage.removeItem('ha_access');
    localStorage.removeItem('ha_active_empresa');
    setAccessToken(null);
    nav('/login');
  }

  const initials = user.name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: open ? 'var(--bg3)' : 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text)',
          maxWidth: 220,
        }}
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt=""
            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0,
            }}
            className="cor-marca"
          >
            {initials}
          </div>
        )}
        <div style={{ minWidth: 0, textAlign: 'left' }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 140,
            }}
          >
            {user.name}
          </div>
        </div>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          style={{
            color: 'var(--text3)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 200,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            zIndex: 60,
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>{roleLabel(user.role)}</div>
          </div>
          <MenuLink to="/conta" icon={Settings} label="Conta" onNavigate={() => setOpen(false)} />
          {showConfig && (
            <MenuLink
              to="/configuracoes"
              icon={Shield}
              label="Configurações"
              onNavigate={() => setOpen(false)}
            />
          )}
          <button
            type="button"
            onClick={() => void handleLogout()}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderTop: '1px solid var(--border)',
              cursor: 'pointer',
              color: 'var(--feedback)',
              fontSize: 13,
            }}
          >
            <LogOut size={16} strokeWidth={1.5} />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  icon: Icon,
  label,
  onNavigate,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        textDecoration: 'none',
        color: 'var(--text)',
        fontSize: 13,
      }}
    >
      <Icon size={16} strokeWidth={1.5} style={{ color: 'var(--text2)' }} />
      {label}
    </Link>
  );
}
